import { useCallback, useEffect, useState } from "react";
import { patientStorageCache } from "./patient-storage-cache";

/**
 * Generic hook for accessing patient-scoped localStorage data.
 *
 * Uses a singleton cache to efficiently share data across multiple hook
 * invocations. Automatically subscribes to changes and re-renders when
 * data is updated by any component.
 *
 * @param patientId - Patient UUID to scope the storage
 * @param namespace - Data namespace (e.g., 'viewedRows', 'bookmarks')
 * @param defaultValue - Default value if namespace doesn't exist
 * @returns Object with data, setData, and updateData methods
 *
 * @example
 * ```typescript
 * const { data, updateData } = usePatientLocalStorage<string[]>(
 *   patientId,
 *   'bookmarks',
 *   []
 * );
 * ```
 */
export function usePatientLocalStorage<T>(patientId: string, namespace: string, defaultValue: T) {
  const [, forceUpdate] = useState({});

  // Subscribe to changes for this patient
  useEffect(() => {
    const unsubscribe = patientStorageCache.subscribe(patientId, () => {
      forceUpdate({});
    });
    return unsubscribe;
  }, [patientId]);

  const data = (patientStorageCache.get(patientId, namespace) as T) ?? defaultValue;

  const setData = useCallback(
    (value: T) => {
      patientStorageCache.set(patientId, namespace, value);
    },
    [patientId, namespace]
  );

  const updateData = useCallback(
    (updater: (prev: T) => T) => {
      const current = (patientStorageCache.get(patientId, namespace) as T) ?? defaultValue;
      patientStorageCache.set(patientId, namespace, updater(current));
    },
    [patientId, namespace, defaultValue]
  );

  return { data, setData, updateData };
}
